import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "@/components/Hero";
import SignupFunnel from "@/components/SignupFunnel";
import { BOOK } from "@/data/book";
import { Button } from "@/components/ui/button";

export default function Landing() {
  return (
    <main data-testid="landing-page">
      <Hero />

      {/* Intro / Before You Begin */}
      <section className="py-24 md:py-32 paper-grain">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <p className="font-ui text-[11px] uppercase tracking-[0.35em] text-primary">Prologue</p>
            <h2 className="font-display text-4xl md:text-5xl text-foreground leading-[1.05] tracking-tight mt-3">
              {BOOK.introTitle}
            </h2>
            <div className="wobble-divider mt-6" />
            <figure className="mt-8 rounded-2xl overflow-hidden border border-border">
              <img src={BOOK.images.intro} alt="Sun peeking over mountain" className="w-full h-72 object-cover" />
            </figure>
          </div>
          <div className="md:col-span-7">
            <div className="max-w-prose">
              {BOOK.intro.split("\n\n").map((p, i) => (
                <p key={`intro-${i}-${p.slice(0, 12)}`} className={`font-body text-lg md:text-xl leading-relaxed text-foreground/90 mb-6 ${i === 0 ? "drop-cap" : ""}`}>
                  {p}
                </p>
              ))}
              <Button asChild variant="link" data-testid="intro-read-cta" className="px-0 font-ui text-primary hover:text-secondary">
                <Link to="/read">
                  Continue to the thirteen truths <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter Index Grid */}
      <section className="py-24 md:py-32 bg-secondary/[0.04] dark:bg-secondary/[0.08]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-ui text-[11px] uppercase tracking-[0.35em] text-primary text-center">The Thirteen</p>
          <h2 className="font-display text-4xl md:text-6xl text-foreground text-center mt-3 tracking-tight leading-none">
            Thirteen Truths.
            <span className="block italic text-secondary mt-1">One field guide.</span>
          </h2>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BOOK.chapters.map((ch) => (
              <Link
                key={ch.id}
                to={`/read#${ch.id}`}
                data-testid={`chapter-card-${ch.id}`}
                className="group relative border border-border bg-card rounded-2xl p-6 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 overflow-hidden"
              >
                <span className="font-ui text-[10px] uppercase tracking-[0.3em] text-primary">
                  Truth {String(ch.number).padStart(2, "0")}
                </span>
                <h3 className="mt-2 font-display text-2xl text-foreground leading-tight tracking-tight">
                  {ch.title}
                </h3>
                <p className="mt-4 font-body italic text-foreground/75 line-clamp-3 text-sm leading-relaxed">
                  “{ch.pullQuote}”
                </p>
                <span className="mt-6 inline-flex items-center font-ui text-xs uppercase tracking-[0.2em] text-secondary group-hover:text-primary transition-colors">
                  Read truth <ArrowRight className="h-3 w-3 ml-2" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <SignupFunnel />

      {/* Closing teaser */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-ui text-[11px] uppercase tracking-[0.35em] text-primary">Pass it on</p>
          <h2 className="font-display text-4xl md:text-5xl text-foreground mt-3 leading-tight tracking-tight">
            Awareness is the whole beginning.
          </h2>
          <div className="wobble-divider mx-auto my-6" />
          <p className="font-body text-lg leading-relaxed text-foreground/80">
            Seeing clearly is itself the turning. The rest follows from there. If something here
            helped you, send it to someone who might need it tonight.
          </p>
          <Button asChild size="lg" data-testid="closing-read-cta" className="mt-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
            <Link to="/read">Read the whole book</Link>
          </Button>
        </div>
      </section>
    </main>
  );
}
